/**
 * Event Dispatcher
 * Routes events to CDN script functions
 */

import type { WindowAnalyzify } from '../types/window-analyzify';
import type { StorefrontProduct, StorefrontCart, StorefrontOrder } from '../types/storefront';
import { PlatformReinitializer } from '../core/platform-reinit';
import { pushGTMPageInfo as pushGTMPageInfoFn } from './init/gtm-page-info';
import { attemptPlatformInit } from './init/platform-init';
import { LANDING_VIEW_KIND, currentPageKey, type LandingViewKind } from '../core/page-key';
import { extractNumericId } from '../adapters/ids';
import type { ShopifyNativeConfig } from '../platforms/shopify-native';
import {
  shopifyPageView,
  shopifyProductView,
  shopifyCollectionView,
  shopifySearchView,
  shopifyAddToCart,
} from '../platforms/shopify-native';
import {
  handleViewItem,
  handleViewItemList,
  handleSelectItem,
  handleAddToCart,
  handleRemoveFromCart,
  handleViewCart,
  handleBeginCheckout,
  handlePurchase,
  handleSearch,
  handleGenerateLead,
  handleAddToWishlist,
  handleVariantChange,
  handleHomepage,
  handleNavClick,
  handlePageView,
} from './handlers';

/**
 * EventDispatcher class
 * Handles routing events to appropriate platform functions
 */
export class EventDispatcher {
  private analyzify: WindowAnalyzify;
  private initAttempted = false;
  private reinitializer: PlatformReinitializer;
  private _shopifyNativeConfig: ShopifyNativeConfig | null = null;

  constructor(analyzify: WindowAnalyzify) {
    this.analyzify = analyzify;
    this.reinitializer = new PlatformReinitializer(analyzify);
    this.hardenTrackMethods();
  }

  /**
   * Analytics must NEVER throw into the host app. Wrap every public trackX
   * method so a throwing/absent CDN or platform function (common in headless,
   * where Liquid-only globals are missing) can never crash the consumer's app.
   * Applied centrally so all handlers — and any future track method — are
   * covered, complementing the per-call guards inside individual handlers.
   */
  private hardenTrackMethods(): void {
    const proto = Object.getPrototypeOf(this);
    for (const name of Object.getOwnPropertyNames(proto)) {
      if (!name.startsWith('track') || typeof (proto as any)[name] !== 'function') continue;
      const original = (proto as any)[name];
      (this as any)[name] = (...args: any[]) => {
        try {
          return original.apply(this, args);
        } catch (err) {
          try {
            this.analyzify?.log?.(`track '${name}' failed: ${err}`, 'dispatcher');
          } catch {
            /* logging must not throw either */
          }
        }
      };
    }
  }

  /**
   * Set the Shopify native analytics config.
   * When set, tracking methods will also send events to Shopify's monorail.
   */
  setShopifyNativeConfig(config: ShopifyNativeConfig | null): void {
    this._shopifyNativeConfig = config;
  }

  /**
   * Update consent state on the Shopify native analytics config.
   * Called when consent changes so subsequent events use the new consent value.
   *
   * When consent flips from false → true, fires a retroactive PAGE_VIEW so
   * Shopify creates a session. Without this, the initial page view (fired
   * before consent) is silently dropped by sendShopifyAnalytics, and visitors
   * who accept cookies but don't navigate further never get a session.
   */
  updateShopifyNativeConsent(hasConsent: boolean, analyticsAllowed?: boolean, marketingAllowed?: boolean): void {
    if (!this._shopifyNativeConfig) return;

    const wasBlocked = !this._shopifyNativeConfig.hasUserConsent;
    this._shopifyNativeConfig.hasUserConsent = hasConsent;
    if (analyticsAllowed !== undefined) this._shopifyNativeConfig.analyticsAllowed = analyticsAllowed;
    if (marketingAllowed !== undefined) this._shopifyNativeConfig.marketingAllowed = marketingAllowed;

    // Retroactive page view: consent just flipped to true → send the page
    // view that was dropped on initial load to create the Shopify session.
    if (wasBlocked && hasConsent) {
      shopifyPageView(
        this.analyzify.shopify_template || 'other',
        this._shopifyNativeConfig
      );
    }
  }

  /**
   * Skip the explicit page-level view call that duplicates what the CDN
   * auto-fires during platform init for the landing page.
   *
   * Called by Analyzify.setPageContext() when it triggers platform
   * initialization. The skip is scoped: it only applies to the call that
   * matches the landing page type (product → viewItem, collection →
   * viewItemList, search → search, cart → viewCart, index → homepage) and
   * only while the visitor is still on the landing page. Any other call — and
   * every call after the first navigation — is tracked normally.
   *
   * The state lives on window.analyzify so SDK instances that reuse the
   * existing initialization (StrictMode, provider re-mounts) see it too.
   */
  suppressNextPageView(pageType: string, pageKey: string): void {
    const kind = LANDING_VIEW_KIND[pageType];
    (this.analyzify as any).__landing_view_skip__ = kind ? { kind, pageKey } : null;
  }

  private shouldSkipPageView(kind: LandingViewKind): boolean {
    const skip = (this.analyzify as any).__landing_view_skip__ as
      | { kind: LandingViewKind; pageKey: string }
      | null
      | undefined;
    if (!skip) return false;
    if (skip.pageKey !== currentPageKey()) {
      // Navigated away from the landing page — the CDN auto-fire no longer applies.
      (this.analyzify as any).__landing_view_skip__ = null;
      return false;
    }
    if (skip.kind !== kind) return false;
    (this.analyzify as any).__landing_view_skip__ = null;
    this.analyzify.log(`Landing ${kind} skipped (CDN auto-fires it during platform init)`, 'dispatcher');
    return true;
  }

  /**
   * Push sh_info and consentUpdate events to GTM dataLayer on SPA navigation.
   *
   * GTM Trigger Groups (used for consent-gated tag firing) require ALL child
   * triggers to fire before the group activates. In traditional Shopify, every
   * page navigation reloads the container, resetting trigger state. In SPA,
   * the container persists, so trigger groups must be re-satisfied explicitly.
   *
   * Two events are needed to complete the typical Trigger Group:
   *   1. sh_info — the "page view" signal (CDN guards this with gtmInitiated flag)
   *   2. consentUpdate — the consent signal (the Liquid consent layer pushes this
   *      to dataLayer, but that snippet isn't loaded in headless)
   *
   * This method pushes both from live window.analyzify state, bypassing CDN's
   * stale closure values (pushedPage, shop, market, userData are captured at
   * initGTM() time and never update on SPA navigation).
   */
  pushGTMPageInfo(): void {
    pushGTMPageInfoFn(this.analyzify);

    // Shopify native analytics: PAGE_VIEW on every SPA navigation
    if (this._shopifyNativeConfig) {
      shopifyPageView(
        this.analyzify.shopify_template || 'other',
        this._shopifyNativeConfig
      );
    }
  }

  /**
   * Fire only the Shopify native PAGE_VIEW event.
   * Used on first page load where pushGTMPageInfo is suppressed
   * (CDN handles its own sh_info during init), but Shopify native
   * has no CDN equivalent so it must fire explicitly.
   */
  fireShopifyNativePageView(): void {
    if (this._shopifyNativeConfig) {
      shopifyPageView(
        this.analyzify.shopify_template || 'other',
        this._shopifyNativeConfig
      );
    }
  }

  /**
   * Attempt platform initialization
   * Called after CDN scripts are loaded
   */
  async attemptInitialization(): Promise<void> {
    if (this.initAttempted) return;
    this.initAttempted = true;
    return attemptPlatformInit(this.analyzify, this.reinitializer);
  }

  /**
   * Register all page-specific CDN functions for SPA navigation
   */
  async registerCDNFunctions(): Promise<void> {
    try {
      await this.reinitializer.registerAllFunctions();
    } catch (error) {
      this.analyzify.log(`Function registration failed: ${error}`, 'dispatcher');
    }
  }

  /**
   * Track view_item event
   */
  trackViewItem(product: StorefrontProduct, variantId?: string): void {
    if (!this.shouldSkipPageView('viewItem')) {
      handleViewItem(this.analyzify, product, variantId);
    }

    // Shopify native analytics: PRODUCT_VIEW
    // Fires regardless of CDN suppression — Shopify native has no CDN auto-fire
    if (this._shopifyNativeConfig) {
      const variant = product.variants?.edges?.find(
        (e) => e.node.id === variantId
      )?.node ?? product.variants?.edges?.[0]?.node;
      shopifyProductView({
        productGid: product.id || '',
        productName: product.title || '',
        productBrand: product.vendor || '',
        variantGid: variant?.id || variantId || '',
        variantPrice: variant?.price?.amount || '0',
        config: this._shopifyNativeConfig,
        variantSku: variant?.sku,
        productCategory: product.productType,
        variantTitle: variant?.title,
      });
    }
  }

  /**
   * Track view_item_list event
   *
   * Note: Uses getCollectionObj from window.analyzify if available (set via setPageContext).
   * For proper collection tracking, call setPageContext({ type: 'collection', collection }) first.
   */
  trackViewItemList(
    products: StorefrontProduct[],
    listName: string,
    listId?: string
  ): void {
    if (!this.shouldSkipPageView('viewItemList')) {
      handleViewItemList(this.analyzify, products, listName, listId);
    }

    // Shopify native analytics: COLLECTION_VIEW for the tracked list
    // Fires regardless of CDN suppression — Shopify native has no CDN auto-fire
    if (this._shopifyNativeConfig && listId) {
      try {
        const numericId = extractNumericId(listId);
        const pageCollection: any = this.analyzify.getCollectionObj;
        const pageCollectionId = pageCollection?.id || pageCollection?.collection?.id;
        const handle =
          (numericId && pageCollectionId && String(pageCollectionId) === String(numericId) ? pageCollection.handle : null) ||
          listName.toLowerCase().replace(/\s+/g, '-');
        shopifyCollectionView(
          numericId ? `gid://shopify/Collection/${numericId}` : '',
          handle,
          this._shopifyNativeConfig
        );
      } catch {
        // Analytics must never throw
      }
    }
  }

  /**
   * Track select_item event (list name, position and list ID as given)
   */
  trackSelectItem(
    product: StorefrontProduct,
    listName: string,
    index?: number,
    listId?: string | number
  ): void {
    handleSelectItem(this.analyzify, product, listName, index, listId);
  }

  /**
   * Track add_to_cart event
   */
  trackAddToCart(
    product: StorefrontProduct,
    quantity: number,
    cart: StorefrontCart,
    variantId?: string
  ): void {
    handleAddToCart(this.analyzify, product, quantity, cart, variantId);

    // Shopify native analytics: ADD_TO_CART
    if (this._shopifyNativeConfig) {
      const variant = product.variants?.edges?.find(
        (e) => e.node.id === variantId
      )?.node ?? product.variants?.edges?.[0]?.node;
      const totalValue = parseFloat(
        cart.cost?.totalAmount?.amount || '0'
      );
      shopifyAddToCart(
        cart.id || '',
        totalValue,
        [{
          productGid: product.id || '',
          variantGid: variant?.id || variantId || '',
          name: product.title || '',
          brand: product.vendor || '',
          price: variant?.price?.amount || '0',
          quantity,
          sku: variant?.sku,
          category: product.productType,
          variantName: variant?.title,
        }],
        this._shopifyNativeConfig
      );
    }
  }

  /**
   * Track remove_from_cart event
   */
  trackRemoveFromCart(
    product: StorefrontProduct,
    quantity: number,
    cart: StorefrontCart,
    variantId?: string
  ): void {
    handleRemoveFromCart(this.analyzify, product, quantity, cart, variantId);
  }

  /**
   * Track view_cart event
   *
   * Note: TikTok does not support view_cart event - only supports:
   * ViewContent, Search, AddToCart, InitiateCheckout
   */
  trackViewCart(cart: StorefrontCart): void {
    if (this.shouldSkipPageView('viewCart')) return;
    handleViewCart(this.analyzify, cart);
    // Shopify native PAGE_VIEW is handled by pushGTMPageInfo / fireShopifyNativePageView
  }

  /**
   * Track begin_checkout event
   */
  trackBeginCheckout(cart: StorefrontCart): void {
    handleBeginCheckout(this.analyzify, cart);
  }

  /**
   * Track purchase event
   *
   * Note: The CDN scripts don't have purchase functions because in traditional
   * Shopify stores, purchase happens on Shopify's checkout which uses Web Pixels.
   *
   * For headless stores, we implement client-side tracking directly calling
   * platform APIs. For production, also implement server-side tracking via
   * GA4 Measurement Protocol and Facebook Conversions API.
   */
  trackPurchase(order: StorefrontOrder): void {
    handlePurchase(this.analyzify, order);
  }

  /**
   * Track search event
   */
  trackSearch(
    term: string,
    results: StorefrontProduct[],
    resultsCount: number
  ): void {
    if (!this.shouldSkipPageView('search')) {
      handleSearch(this.analyzify, term, results, resultsCount);
    }

    // Shopify native analytics: SEARCH_VIEW
    // Fires regardless of CDN suppression — Shopify native has no CDN auto-fire
    if (this._shopifyNativeConfig) {
      shopifySearchView(term, this._shopifyNativeConfig);
    }
  }

  /**
   * Track generate_lead event
   *
   * Note: CDN scripts don't have dedicated generate_lead/lead functions.
   * Lead tracking in the extension is handled by f-form-listener.js which
   * calls platform APIs directly. We replicate that behavior here.
   */
  trackGenerateLead(formData: Record<string, any>): void {
    handleGenerateLead(this.analyzify, formData);
  }

  /**
   * Track add_to_wishlist event
   */
  trackAddToWishlist(product: StorefrontProduct, variantId?: string): void {
    handleAddToWishlist(this.analyzify, product, variantId);
  }

  /**
   * Track variant_change event
   */
  trackVariantChange(product: StorefrontProduct, variantId: string): void {
    handleVariantChange(this.analyzify, product, variantId);
  }

  /**
   * Track homepage view event
   * Call this when user lands on the homepage
   */
  trackHomepage(): void {
    if (this.shouldSkipPageView('homepage')) return;
    handleHomepage(this.analyzify);
    // Shopify native PAGE_VIEW is handled by pushGTMPageInfo / fireShopifyNativePageView
  }

  /**
   * Track navigation click event (ee_nav_click)
   * Fires when a user clicks a navigation menu item that triggers page navigation.
   * Matches the Liquid theme's GTM event format.
   */
  trackNavClick(title: string, url: string, position: string): void {
    handleNavClick(this.analyzify, title, url, position);
  }

  /**
   * Track server-side page_view event
   * Called on SPA navigation to send page_view to analyzifycdn
   */
  trackPageView(): void {
    handlePageView(this.analyzify);
  }

  /**
   * Trigger cart upsert check for SPA navigation
   * Re-runs analyzify_checksendcartdata() which only fires during platform init normally.
   * Resets _sharedCartIdAttempts so the CDN retries cart ID detection on new pages.
   *
   * Caller (analyzify.ts) must set window.analyzify._isSameDomainNavigation
   * before invoking this method so the CDN knows whether to apply its
   * changehash dedup. The CDN's native document.referrer check is stale in
   * SPA contexts because pushState never updates document.referrer.
   */
  triggerCartUpsertCheck(): void {
    const win = window as any;
    if (typeof win.analyzify_checksendcartdata === 'function') {
      if (win.analyzify?._sharedCartIdAttempts !== undefined) {
        win.analyzify._sharedCartIdAttempts = 0;
      }
      win.analyzify_checksendcartdata();
      this.analyzify.log('Cart upsert check triggered for SPA navigation', 'dispatcher');
    }
  }

}
